// Local dates and day boundaries in the circle's zone.
//
// "Tuesday" is not a duration. In Europe/London it is 24 hours for most of the
// year, 23 hours on the last Sunday in March, and 25 hours on the last Sunday in
// October. Every function here derives the answer from the zone rather than
// assuming 86,400,000 milliseconds, because that assumption is exactly how a
// coverage calculation quietly loses or gains an hour twice a year (R-024).

import type { Instant, TimeZoneId, WallClock } from "./types.js";
import { resolveWallClock, toWallClock } from "./zone.js";

const MINUTE_MS = 60_000;

/**
 * A calendar date in the circle's zone, as `YYYY-MM-DD`.
 *
 * Plan §28.12 requires local-date intent to be *preserved*, not collapsed into
 * an instant — "Tuesday afternoon" means a wall-clock day, and which instants
 * that day contains depends on the zone. Plan §28.7 stores exactly this shape
 * for `weekStartLocal`.
 *
 * Branded so it cannot be confused with any other string, and in particular so
 * an ISO *timestamp* cannot be passed where a date is expected.
 */
export type LocalDate = string & { readonly __brand: unique symbol };

const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Narrow a `YYYY-MM-DD` string to a {@link LocalDate}. Throws on anything else. */
export function localDate(value: string): LocalDate {
  if (!LOCAL_DATE_PATTERN.test(value)) {
    throw new RangeError(`LocalDate must be YYYY-MM-DD, got ${value}`);
  }
  const [year, month, day] = value.split("-").map(Number) as [number, number, number];
  // Reject 2026-02-31 and friends: round-tripping through UTC catches any
  // day-of-month that does not exist in that month.
  const roundTrip = new Date(Date.UTC(year, month - 1, day));
  if (
    roundTrip.getUTCFullYear() !== year ||
    roundTrip.getUTCMonth() !== month - 1 ||
    roundTrip.getUTCDate() !== day
  ) {
    throw new RangeError(`LocalDate is not a real calendar date: ${value}`);
  }
  return value as LocalDate;
}

/** Build a {@link LocalDate} from calendar parts. */
export function localDateOfParts(year: number, month: number, day: number): LocalDate {
  const pad = (n: number, width: number): string => String(n).padStart(width, "0");
  return localDate(`${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`);
}

/** Which local date an instant falls on, in this zone. */
export function localDateOf(at: Instant, zone: TimeZoneId): LocalDate {
  const wall = toWallClock(at, zone);
  return localDateOfParts(wall.year, wall.month, wall.day);
}

/** The day after `date`, by calendar arithmetic (never by adding 24 hours). */
export function nextLocalDate(date: LocalDate): LocalDate {
  const [year, month, day] = date.split("-").map(Number) as [number, number, number];
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  return localDateOfParts(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate());
}

/**
 * The Monday on or before `date` — the week a local date belongs to.
 *
 * Weeks begin on Monday throughout this product (plan §28.7 stores
 * `weekStartLocal`, and the availability grid and week views all assume it), so
 * "which week is this?" has one answer and every surface asks for it here rather
 * than working it out again.
 *
 * DELIBERATELY CALENDAR ARITHMETIC, NOT INSTANT ARITHMETIC. A `LocalDate` is
 * already a wall-clock day, so no zone is needed and none is taken: subtracting
 * days from a date cannot cross a DST boundary because it never touches a
 * timeline. Doing the same thing by subtracting 86,400,000 milliseconds from an
 * instant is precisely the bug R-024 rates Critical — it lands on Sunday 23:00
 * twice a year and silently reports the wrong week.
 *
 * `Date.UTC` here is a calendar calculator, not a timezone: the parts go in and
 * come back out unshifted, which is why UTC is the correct — and only safe —
 * choice for it.
 */
export function startOfLocalWeek(date: LocalDate): LocalDate {
  const [year, month, day] = date.split("-").map(Number) as [number, number, number];
  const at = new Date(Date.UTC(year, month - 1, day));
  // getUTCDay: 0 = Sunday. Sunday belongs to the week that began six days ago,
  // not to the one starting tomorrow.
  const daysSinceMonday = (at.getUTCDay() + 6) % 7;

  const monday = new Date(Date.UTC(year, month - 1, day - daysSinceMonday));
  return localDateOfParts(monday.getUTCFullYear(), monday.getUTCMonth() + 1, monday.getUTCDate());
}

/**
 * The same weekday, `weeks` weeks away — forward for positive, back for negative.
 *
 * CALENDAR ARITHMETIC, for the reason `startOfLocalWeek` gives at length: a
 * `LocalDate` is a wall-clock day and never touches a timeline, so moving seven
 * days cannot land on the hour a zone deleted or repeated. Adding
 * `7 * 24 * 60 * 60 * 1000` to an instant instead is the bug R-024 rates Critical,
 * and it goes wrong twice a year in exactly the week somebody is planning.
 *
 * Here so the week view can page (bead hf-rtf) without doing date maths in a
 * component — invariant 9 puts every calendar calculation in this package, and a
 * pager that walked weeks in the browser would be the first exception.
 */
export function addLocalWeeks(date: LocalDate, weeks: number): LocalDate {
  const [year, month, day] = date.split("-").map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(year, month - 1, day + weeks * 7));
  return localDateOfParts(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth() + 1,
    shifted.getUTCDate(),
  );
}

/**
 * The first instant of a local day.
 *
 * Midnight itself does not always exist. Some zones have historically shifted
 * *at* midnight, which deletes 00:00 entirely; when that happens this walks
 * forward minute by minute to the first wall time that does exist, so the day
 * still has a defined beginning rather than throwing at the caller.
 *
 * Europe/London transitions at 01:00, so this fast path is what runs in practice
 * — but the product is not hard-coded to one zone, and a day with no start would
 * be a nasty way to discover that.
 */
export function startOfLocalDay(date: LocalDate, zone: TimeZoneId): Instant {
  const [year, month, day] = date.split("-").map(Number) as [number, number, number];

  for (let minuteOfDay = 0; minuteOfDay < 24 * 60; minuteOfDay += 1) {
    const wall: WallClock = {
      year,
      month,
      day,
      hour: Math.floor(minuteOfDay / 60),
      minute: minuteOfDay % 60,
    };
    const resolved = resolveWallClock(wall, zone);
    if (resolved.kind === "ok") return resolved.instant;
    // On a fall-back day the first minutes may be ambiguous; the day begins at
    // the earlier of the two, which is the one that comes first on the timeline.
    if (resolved.kind === "ambiguous") return resolved.earlier;
  }

  throw new RangeError(`No wall-clock time exists on ${date} in ${zone}`);
}

/**
 * The instant the local day ends, EXCLUSIVE — i.e. the first instant of the next
 * day.
 *
 * Exclusive on purpose: windows in this module are half-open `[start, end)`, so
 * a window may end exactly here and still belong entirely to `date`.
 */
export function endOfLocalDayExclusive(date: LocalDate, zone: TimeZoneId): Instant {
  return startOfLocalDay(nextLocalDate(date), zone);
}

/**
 * How long a local day actually is, in minutes.
 *
 * 1440 on an ordinary day, 1380 on the spring-forward day, 1500 on the
 * fall-back day. Anything in the product that needs "a day" must ask this rather
 * than multiply.
 */
export function localDayLengthMinutes(date: LocalDate, zone: TimeZoneId): number {
  const start = startOfLocalDay(date, zone);
  const end = endOfLocalDayExclusive(date, zone);
  return (end - start) / MINUTE_MS;
}
